import { useEffect, useMemo, useState } from 'react';

import EditIcon from '@mui/icons-material/Edit';
import { Box, Grid, IconButton, Stack } from '@mui/material';
import { ResourceType } from '@prisma/client';
import { Resource, User } from 'types/graphql';

import { useMutation, useQuery } from '@redwoodjs/web';

import { NPILogo } from 'src/assets/images';
import Loader from 'src/components/Loader/Loader';
import { ModalBox } from 'src/components/ModalContent/Modal';
import ResourceForm from 'src/components/ResourceForm';
import { showErrorToast, showSuccessToast } from 'src/components/Toasts/toast';
import { EDIT_PARTNER_RESOURCES_MUTATION } from 'src/graphql/mutations/editPartnerResources.mutation';
import { GET_USER } from 'src/graphql/queries/users.query';
import useGeneratePresignedUrl from 'src/hooks/useGeneratePresignedUrl';
import { LogError } from 'src/lib';
import { formatDate, getEnumDropDownOptions } from 'src/utils/utils';
import { partnerResourceValidationSchema } from 'src/utils/validations';

import ResourceView from './ResourceView';

interface ViewResourceModalProps {
    open: boolean;
    handleCloseModal: () => void;
    refetchLatestResources: () => void;
    handleEditResourceModal: () => void;
    showEditResourceModal: boolean;
    resource: Resource;
    partnerResourceSections: Partial<Resource>[];
    showEditIcon?: boolean;
}

const ViewResourceModal = ({
    open,
    handleCloseModal,
    resource,
    partnerResourceSections,
    refetchLatestResources,
    handleEditResourceModal,
    showEditResourceModal,
    showEditIcon = false,
}: ViewResourceModalProps) => {
    const [fullName, setFullName] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [resourceUrl, setResourceUrl] = useState('');

    const { validateAwsURL } = useGeneratePresignedUrl();

    const editResourceInitialValues = useMemo(() => {
        return {
            resource_name: resource?.name,
            description: resource?.description,
            viewAccessPermissions: resource?.permission,
            resource_type: resource?.type,
            resourceURL: resource?.resourceUrl,
            imageUrl: resource?.imageUrl,
            section: resource?.section.id,
            id: resource?.id,
            status: resource?.status?.props?.status ?? resource?.status,
        };
    }, [resource]);

    const { loading } = useQuery(GET_USER, {
        variables: { id: resource?.createdById },
        skip: !resource,
        onCompleted: (data) => {
            const user = data?.user as User;
            setFullName(`${user.firstName} ${user.lastName}`);
        },
        onError: (error) => {
            showErrorToast('There was an error retrieving the user who added this resource');
            LogError(error);
        },
    });

    const ResourceGridContainer = ({ children }) => {
        return (
            <Grid container spacing={0} sx={{ alignItems: 'stretch', marginBottom: 3 }}>
                {children}
            </Grid>
        );
    };

    const ResourceGridItem = ({ children, xs = 4 }) => {
        return (
            <Grid
                item
                xs={xs}
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                }}
            >
                {children}
            </Grid>
        );
    };

    const [editResourceMutation] = useMutation(EDIT_PARTNER_RESOURCES_MUTATION, {
        onCompleted: () => {
            refetchLatestResources();
            showSuccessToast('Resource Edited!');
            handleEditResourceModal();
        },
        onError: (error) => {
            LogError(error);
            showErrorToast(error?.message ?? 'Error Editing Resource');
        },
    });

    const getSectionDropDownOptions = () => {
        return (
            partnerResourceSections?.map((value) => {
                return {
                    label: value.name.replace(/_/g, ' '),
                    value: value.id,
                };
            }) ?? []
        );
    };

    useEffect(() => {
        // reset image and resource url states to not cause ui flicker
        setImageUrl('');
        setResourceUrl('');

        const validateImageUrl = async () => {
            if (!resource?.imageUrl.includes('amazonaws.com')) {
                setImageUrl(resource?.imageUrl);
                return;
            }

            const updatedUrl = await validateAwsURL(resource?.imageUrl);
            if (updatedUrl) {
                setImageUrl(updatedUrl);
            }
        };

        const validateResourceUrl = async () => {
            if (!resource?.resourceUrl.includes('amazonaws.com')) {
                setResourceUrl(resource?.resourceUrl);
                return;
            }

            const updatedUrl = await validateAwsURL(resource?.resourceUrl);
            if (updatedUrl) {
                setResourceUrl(updatedUrl);
            }
        };

        validateImageUrl();
        validateResourceUrl();
    }, [resource]);

    return (
        <>
            <ModalBox
                open={open}
                handleCloseModal={handleCloseModal}
                title="View Resource"
                widthSize="640px"
            >
                <Stack spacing={3}>
                    <Box p={1}>
                        <ResourceGridContainer>
                            <Grid
                                item
                                xs={9}
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    mt: 1,
                                }}
                            >
                                <label className="mb-5 font-bold">Resource Name</label>
                                {resource?.name}
                            </Grid>
                            {showEditIcon && (
                                <Grid
                                    item
                                    xs={3}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'start',
                                        alignItems: 'flex-end',
                                    }}
                                >
                                    <IconButton
                                        onClick={() => {
                                            handleCloseModal();
                                            handleEditResourceModal();
                                        }}
                                    >
                                        <EditIcon />
                                    </IconButton>
                                </Grid>
                            )}
                        </ResourceGridContainer>

                        <ResourceGridContainer>
                            <ResourceGridItem>
                                <label className="mb-5 font-bold">Section</label>
                                {resource?.section.name}
                            </ResourceGridItem>
                            <ResourceGridItem>
                                <label className="mb-5 font-bold">Date Added</label>
                                {formatDate({ date: new Date(resource?.createdAt) })}
                            </ResourceGridItem>
                        </ResourceGridContainer>

                        <ResourceGridContainer>
                            <ResourceGridItem>
                                <label className="mb-5 font-bold">Resource Type</label>
                                {resource?.type}
                            </ResourceGridItem>
                            <ResourceGridItem>
                                <label className="mb-5 font-bold">Added By</label>
                                {loading ? <Loader /> : fullName}
                            </ResourceGridItem>
                        </ResourceGridContainer>

                        <ResourceGridContainer>
                            <ResourceGridItem xs={12}>
                                <label className="mb-5 font-bold">Description</label>
                                {resource?.description}
                            </ResourceGridItem>
                        </ResourceGridContainer>

                        <ResourceGridContainer>
                            <ResourceGridItem>
                                <label className="mb-5 font-bold">Image</label>

                                {resource?.imageUrl ? (
                                    <img src={imageUrl} width="70" alt={resource?.name} />
                                ) : (
                                    <NPILogo />
                                )}
                            </ResourceGridItem>
                        </ResourceGridContainer>

                        <ResourceGridContainer>
                            <ResourceGridItem xs={12}>
                                <ResourceView type={resource?.type} url={resourceUrl} />
                            </ResourceGridItem>
                        </ResourceGridContainer>
                    </Box>
                </Stack>
            </ModalBox>
            <ModalBox
                handleCloseModal={() => {
                    return handleEditResourceModal();
                }}
                open={showEditResourceModal}
                widthSize="800px"
                title="Edit Resource"
            >
                <ResourceForm
                    isEditMode
                    initialValues={editResourceInitialValues}
                    sectionsOptions={getSectionDropDownOptions()}
                    resourceTypeOptions={getEnumDropDownOptions(ResourceType)}
                    onCancel={() => {
                        return handleEditResourceModal();
                    }}
                    onSubmit={(formik) => {
                        editResourceMutation({
                            variables: {
                                input: {
                                    id: formik.id,
                                    name: formik.resource_name,
                                    description: formik.description,
                                    type: formik.resource_type,
                                    imageUrl: formik.imageUrl,
                                    resourceUrl: formik.resourceURL,
                                    sectionId: formik.section,
                                    status: formik?.status?.props?.status ?? formik.status,
                                    permission: formik.viewAccessPermissions,
                                },
                            },
                        });
                    }}
                    validationSchema={partnerResourceValidationSchema}
                />
            </ModalBox>
        </>
    );
};

export default ViewResourceModal;
